import { Clock, MapPin, Phone, ShieldAlert, Star } from 'lucide-react';
import { formatGbpHours } from '@pageboss/shared';
import type { GbpLocationView, GbpWeekday } from '@pageboss/shared';
import { cn } from '@/lib/utils';
import { StaleStamp } from './part-status';

interface GbpLocationCardProps {
  location: GbpLocationView;
  /** True only when Google has explicitly told us this listing is
   *  unverified (connection.hasVoiceOfMerchant === false). Null/unknown
   *  never renders the banner — asserting "unverified" without knowing it
   *  would be the same kind of fabrication as a made-up number. */
  unverified: boolean;
  /** Set only when the location part is showing a stale (failed-refetch)
   *  snapshot — never on a fresh fetch (design brief §5.6). */
  staleFetchedAt: number | undefined;
}

const DAY_ORDER: GbpWeekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DAY_LABEL: Record<GbpWeekday, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

export function GbpLocationCard({ location, unverified, staleFetchedAt }: GbpLocationCardProps) {
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  // reviewCount is only ever absent when the reviews fetch has never
  // succeeded once (site-gbp.tsx's toLocationView) — the real API always
  // returns a (possibly 0) count on a successful/stale-but-present read.
  // That makes it, not rating, the signal for "do we actually know": a
  // real zero (reviewCount === 0, rating omitted because averageRating is
  // null at zero reviews) must read as "No reviews yet.", but an unknown
  // count (fetch failed, nothing to fall back on) must not collapse into
  // the same sentence — that would assert a zero we don't actually have.
  const reviewCount = location.reviewCount;
  const rating = location.rating;
  return (
    <section className="rounded-lg border border-border-subtle bg-card p-6">
      <header className="mb-5">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold tracking-tight">{location.name}</h2>
          <StaleStamp fetchedAt={staleFetchedAt} />
        </div>
        {unverified ? (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-fg">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              Google hasn't verified this business yet, so some changes can't go through until it
              is.
            </span>
          </div>
        ) : null}
        {location.category ? (
          <p className="mt-1 text-sm text-text-secondary">{location.category}</p>
        ) : null}
        {typeof reviewCount === 'number' ? (
          reviewCount > 0 ? (
            <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-foreground">
              {typeof rating === 'number' ? (
                <>
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden />
                  <span className="font-medium">{rating.toFixed(1)}</span>
                </>
              ) : null}
              <span className="text-text-tertiary">· {reviewCount.toLocaleString()} reviews</span>
            </div>
          ) : (
            <p className="mt-2 text-sm text-text-tertiary">No reviews yet.</p>
          )
        ) : (
          <p className="mt-2 text-sm text-text-tertiary">Review count unavailable right now.</p>
        )}
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-3 text-sm">
          {location.address ? (
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
              <div>
                {location.address.lines.length > 0 ? (
                  <div className="text-foreground">{location.address.lines.join(', ')}</div>
                ) : null}
                <div className="text-text-secondary">
                  {[location.address.city, location.address.region, location.address.postalCode]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              </div>
            </div>
          ) : location.serviceArea ? (
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
              <div className="text-text-secondary">Service-area business — no public storefront address.</div>
            </div>
          ) : null}
          {location.phone ? (
            <div className="flex items-start gap-2">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
              <a
                href={`tel:${location.phone.replace(/\s+/g, '')}`}
                className="text-foreground hover:text-text-secondary"
              >
                {location.phone}
              </a>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 text-sm">
          <div className="inline-flex items-center gap-2 text-text-tertiary">
            <Clock className="h-4 w-4" aria-hidden />
            <span className="eyebrow">Hours</span>
          </div>
          {location.hours ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              {DAY_ORDER.map((day) => {
                const hours = location.hours?.[day];
                const isToday = day === today;
                return (
                  <div key={day} className="contents">
                    <dt
                      className={cn(
                        'text-xs',
                        isToday ? 'font-semibold text-foreground' : 'text-text-secondary',
                      )}
                    >
                      {DAY_LABEL[day]}
                    </dt>
                    <dd
                      className={cn(
                        'text-xs',
                        isToday ? 'font-semibold text-foreground' : 'text-text-secondary',
                      )}
                    >
                      {hours ? formatGbpHours(hours) : '—'}
                    </dd>
                  </div>
                );
              })}
            </dl>
          ) : (
            <p className="text-xs text-text-tertiary">Hours not available.</p>
          )}
        </div>
      </div>
    </section>
  );
}
